import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppProvider, useApp } from "@/contexts/AppContext";
import { trackPageView } from "@/lib/analytics";
import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Index from "./pages/Index";
import SearchPage from "./pages/SearchPage";
import ListingDetail from "./pages/ListingDetail";
import ShortRedirect from "@/pages/ShortRedirect";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import Dashboard from "./pages/Dashboard";
import FavoritesPage from "./pages/FavoritesPage";
import CreateListing from "./pages/CreateListing";
import TermosPage from "./pages/TermosPage";
import PrivacidadePage from "./pages/PrivacidadePage";
import NotFound from "./pages/NotFound";
import ProfilePage from "./pages/ProfilePage";
import AdminPage from "./pages/AdminPage";
import AdminReportsPage from "./pages/AdminReportsPage";
import MessagesPage from "./pages/MessagesPage";
import EditListing from "./pages/EditListing";
import ComoFunciona from "./pages/ComoFunciona";
import Beneficios from "./pages/Beneficios";
import RedefinirSenhaPage from "./pages/RedefinirSenhaPage";
import DownloadPage from "./pages/DownloadPage";
import PlansPage from "./pages/PlansPage";
import AffiliatePage from "./pages/AffiliatePage";
import StorePage from "./pages/StorePage";

const queryClient = new QueryClient();

function AppRoutes() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname, !!user);
  }, [location.pathname]);

  // Captura ?ref= de afiliado em qualquer página e guarda
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) localStorage.setItem("affiliateRef", ref);
  }

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const setupBackButton = async () => {
      const { App: CapApp } = await import("@capacitor/app");
      await CapApp.addListener("backButton", ({ canGoBack }) => {
        if (canGoBack) {
          window.history.back();
        } else {
          CapApp.exitApp();
        }
      });
    };

    setupBackButton();

    return () => {
      import("@capacitor/app").then(({ App: CapApp }) => {
        CapApp.removeAllListeners();
      });
    };
  }, []);
  const { authLoading, user } = useApp();
  const navigate = useNavigate();

  useEffect(() => {
    // Capturar tokens do Supabase na URL (recuperacao de senha, confirmacao de email)
    const hash = window.location.hash;
    if (hash.includes("access_token") && hash.includes("type=recovery")) {
      navigate("/redefinir-senha");
    }
  }, []);

  if (authLoading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="flex flex-col items-center gap-3">
        <div className="h-10 w-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-muted-foreground">Carregando...</p>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/busca" element={<SearchPage />} />
          <Route path="/anuncio/:id" element={<ListingDetail />} />
          <Route path="/a/:code" element={<ShortRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/cadastro" element={<RegisterPage />} />
          <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/favoritos" element={<FavoritesPage />} />
          <Route path="/criar-anuncio" element={<CreateListing />} />
          <Route path="/como-funciona" element={<ComoFunciona />} />
          <Route path="/beneficios" element={<Beneficios />} />
          <Route path="/mensagens" element={<MessagesPage />} />
          <Route path="/perfil" element={<ProfilePage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/relatorios" element={<AdminReportsPage />} />
          <Route path="/admin/relatorios/:id" element={<AdminReportsPage />} />
          <Route path="/editar-anuncio/:id" element={<EditListing />} />
          <Route path="/termos" element={<TermosPage />} />
          <Route path="/privacidade" element={<PrivacidadePage />} />
          <Route path="/redefinir-senha" element={<RedefinirSenhaPage />} />
          <Route path="/download" element={<DownloadPage />} />
          <Route path="/planos" element={<PlansPage />} />
          <Route path="/afiliado" element={<AffiliatePage />} />
          <Route path="/loja/:code" element={<StorePage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AppProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AppProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
